import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Flame, Snowflake, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import { SPORT_META } from "@/lib/odds";
import { SportTabs } from "@/components/SportTabs";
import { FollowButton } from "@/components/FollowButton";

export function StreakBadge({ record, size = "sm" }) {
  const hot = record?.hot, cold = record?.cold;
  if (!hot && !cold) return null;
  const s = hot || cold;
  const cls = size === "lg" ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]";
  return (
    <span data-testid={hot ? "streak-hot" : "streak-cold"} className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wider ${cls} ${hot ? "border-orange-500/50 bg-orange-500/10 text-orange-300" : "border-sky-500/50 bg-sky-500/10 text-sky-300"}`}>
      {hot ? <Flame size={11} /> : <Snowflake size={11} />} {s.streak} straight {hot ? "hits" : "misses"} · {s.stat}
    </span>
  );
}

function PlayerCard({ p, i }) {
  const meta = SPORT_META[p.sport];
  const r = p.record;
  return (
    <Link to={`/players/${p.slug}`} data-testid="player-card" className="glass-card fade-up group block p-4 transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:!border-emerald-500/50" style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}>
      <div className="flex items-center gap-2">
        <span className="label-caps" style={{ color: meta?.color }}>{meta?.short}</span>
        <span className="rounded bg-[#0B0E14] px-1.5 py-0.5 font-mono-num text-[10px] text-slate-400">{p.team}</span>
        <span className="ml-auto"><FollowButton slug={p.slug} name={p.name} /></span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <h3 className="font-display text-2xl font-bold leading-none tracking-tight text-white group-hover:text-emerald-300" data-testid="player-card-name">{p.name}</h3>
        {p.playing_today ? <span className="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-400" data-testid="player-playing-today"><CalendarCheck size={11} /> Today · {p.props} props</span> : <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-600">Not on slate</span>}
      </div>
      <p className="mt-1 truncate text-xs text-slate-500">{p.matchup || `${p.league}`}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {r.n > 0 ? (
          <span className="font-mono-num text-sm text-slate-300" data-testid="player-card-record">
            <span className={`font-bold ${r.hit_rate >= 55 ? "text-emerald-400" : r.hit_rate < 45 ? "text-red-400" : "text-amber-400"}`}>{r.hit_rate}%</span> · {r.won}–{r.lost} model calls
          </span>
        ) : (
          <span className="text-xs text-slate-500">No model history yet</span>
        )}
        <StreakBadge record={r} />
      </div>
    </Link>
  );
}

export default function Players() {
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("all");
  const [d, setD] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/players?q=${encodeURIComponent(q)}&sport=${sport}`).then((r) => setD(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, sport]);
  const hot = d?.players.filter((p) => p.record.hot).length || 0;

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="players-page">
      <p className="label-caps text-emerald-400">Player pages</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Players & Streaks</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Every player the model has called a prop on: today's ladders, how the Lab's calls on them have hit over the last 3 weeks, and who is riding a hot or cold streak right now.</p>
      <div className="mt-6 relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input data-testid="player-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a player, team or sport — e.g. Harper, Celtics, Alcaraz" className="input-dark !pl-9" />
      </div>
      <div className="mt-4"><SportTabs value={sport} onChange={setSport} /></div>
      <p className="mt-3 text-xs text-slate-500" data-testid="players-count">{d ? `${d.count} players · ${hot} on a hot streak` : "Loading…"}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="players-grid">
        {d?.players.map((p, i) => <PlayerCard key={p.slug} p={p} i={i} />)}
        {d && d.players.length === 0 && <p className="text-sm text-slate-500" data-testid="players-empty">No players match that search.</p>}
      </div>
    </main>
  );
}
